import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { plusWidgetArticleHomeV2$key } from "scmp-app/queries/__generated__/plusWidgetArticleHomeV2.graphql";

import { Container, CoverLink, HeadlineLink, InlineCover, Timestamp } from "./styles";

type Props = {
  className?: string;
  reference: plusWidgetArticleHomeV2$key;
  variant?: Variant;
};

type Variant = "cover" | "lead" | "text";

export const HomeV2PlusWidgetArticle: FunctionComponent<Props> = ({
  className,
  reference,
  variant = "text",
}) => {
  const entity = useFragment(
    graphql`
      fragment plusWidgetArticleHomeV2 on Content {
        entityId
        urlAlias
        ...entityLink
        ...hooksContentItemProvidersContent
          @arguments(
            withHeadline: true
            withPublishedDate: true
            withCoverImage: true
            withCoverImageSize540x360: true
          )
      }
    `,
    reference,
  );
  const providers = useContentItemProviders(entity);

  const linkQuery = { display: "plus", module: "content_discovery", pgtype: "homepage" };

  const handleItemClickEvent = () => {
    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        article_id: entity.entityId ?? "",
        destination_url: entity.urlAlias ?? "",
        page_type: "homepage",
        widget_name: "scmp_plus",
      },
      subcategory: "widget",
    });
  };

  const coverImage = (
    <CoverLink onClick={handleItemClickEvent} query={linkQuery} reference={entity}>
      {providers.coverImage({
        responsiveVariants: {
          desktopUp: "size540x360",
          mobileUp: "size540x360",
          tabletUp: "size540x360",
        },
      })}
    </CoverLink>
  );

  if (variant === "cover") return coverImage;

  return (
    <Container $isLead={variant === "lead"} className={className}>
      <HeadlineLink onClick={handleItemClickEvent} query={linkQuery} reference={entity}>
        {providers.headline({ hideTooltip: true, preferSocialHeadline: true })}
      </HeadlineLink>
      <Timestamp>{providers.publishedDate({ displayMode: "relative" })}</Timestamp>
      {variant === "lead" && <InlineCover>{coverImage}</InlineCover>}
    </Container>
  );
};

HomeV2PlusWidgetArticle.displayName = "HomeV2PlusWidgetArticle";
